"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Calendar as CalendarIcon, MapPin, Clock, Users, MessageSquare, Loader as Loader2 } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
const VEHICLE_TYPES = ["Luxury Sedan (up to 3 pax)", "Premium SUV (up to 6 pax)", "Executive Sprinter (up to 14 pax)"];
const SERVICE_TYPES = [{
  id: "distance",
  label: "Distance Based"
}, {
  id: "hourly",
  label: "Hourly Rate"
}, {
  id: "flat",
  label: "Flat Rate"
}];
export default function TabbedBooking() {
  return;
}
function BookingFormContent() {
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: {
      isValid,
      errors
    }
  } = useWvcForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const activeTab = watch("service_type");
  const onSubmit = (data: any) => {
    setIsSubmitting(true);
    console.log("Form submission triggered");
    console.log("Form validity:", isValid);
    console.log("Validation errors:", errors);
    const phoneNumber = "13234926292";
    const dateStr = data.pickupDate ? format(data.pickupDate, "PPP") : "N/A";
    const message = `New Booking Request:
Service Type: ${data.service_type}
Pickup: ${data.pickupLocation}
Drop-off: ${data.dropoffLocation}
Date: ${dateStr}
Time: ${data.pickupTime}
Vehicle: ${data.vehicleType}
Passengers: ${data.passengers}`;
    console.log("Opening WhatsApp with message:", message);
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, "_blank");
    setTimeout(() => {
      setIsSubmitting(false);
    }, 2000);
  };
  return <div className="glass-effect bg-white/[0.03] backdrop-blur-2xl rounded-lg p-6 md:p-8 shadow-strong border border-white/10 relative overflow-hidden">
      <div className="absolute inset-0 bg-linear-to-br from-primary/5 to-transparent pointer-events-none" />
      
      <div className="relative z-10 mb-8">
        <div className="flex flex-wrap gap-2 p-1 bg-white/5 rounded-lg border border-white/10">
          {SERVICE_TYPES.map((type, i) => <Button key={type.id} data-index={i} type="button" variant="ghost" onClick={() => setValue("service_type", type.id)} className={cn("flex-1 h-10 rounded-md text-xs font-bold uppercase tracking-wider transition-all duration-300", activeTab === type.id ? "bg-primary text-black shadow-lg hover:bg-primary/90" : "text-white/60 hover:text-white hover:bg-white/10")}>
              {type.label}
            </Button>)}
        </div>
      </div>

      <Form onSubmit={e => {
      if (!isValid) {
        console.log("Form is invalid, preventing submission");
        return;
      }
      handleSubmit(onSubmit)(e);
    }} className="relative z-10 space-y-6">
        <div className="grid grid-cols-1 gap-4">
          <FormField control={control} name="pickupLocation" render={({
          field
        }) => <FormItem>
              <FormLabel className="text-white text-xs font-bold uppercase tracking-wider">Pickup Location *</FormLabel>
              <FormControl>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
                  <Input placeholder="Enter pickup address or airport" className="pl-10 bg-white/5 border-white/10 text-white h-11" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

          <FormField control={control} name="dropoffLocation" render={({
          field
        }) => <FormItem>
              <FormLabel className="text-white text-xs font-bold uppercase tracking-wider">Drop-off Location *</FormLabel>
              <FormControl>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
                  <Input placeholder="Enter drop-off address" className="pl-10 bg-white/5 border-white/10 text-white h-11" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField control={control} name="pickupDate" render={({
            field
          }) => <FormItem className="flex flex-col">
                <FormLabel className="text-white text-xs font-bold uppercase tracking-wider">Pickup Date *</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button variant="outline" className={cn("w-full pl-3 text-left font-normal bg-white/5 border-white/10 h-11 text-white hover:bg-white/10", !field.value && "text-white/40")}>
                        {field.value ? format(field.value, "PPP") : <span>Pick a date</span>}
                        <SafeHydrate><CalendarIcon className="ml-auto h-4 w-4 text-primary" /></SafeHydrate>
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-black border-primary/20" align="start">
                    <SafeHydrate><Calendar mode="single" selected={field.value} onSelect={field.onChange} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus /></SafeHydrate>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>} />

            <FormField control={control} name="pickupTime" render={({
            field
          }) => <FormItem>
                <FormLabel className="text-white text-xs font-bold uppercase tracking-wider">Pickup Time *</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
                    <Input type="time" className="pl-10 bg-white/5 border-white/10 text-white h-11" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField control={control} name="vehicleType" render={({
            field
          }) => <FormItem>
                <FormLabel className="text-white text-xs font-bold uppercase tracking-wider">Vehicle Type *</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-11 w-full">
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-black border-primary/20">
                    {VEHICLE_TYPES.map((type, i) => <SelectItem key={type} value={type} data-index={i} className="text-white focus:bg-primary/20">
                        {type}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />

            <FormField control={control} name="passengers" render={({
            field
          }) => <FormItem>
                <FormLabel className="text-white text-xs font-bold uppercase tracking-wider">Passengers *</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
                    <Input type="number" min="1" max="14" className="pl-10 bg-white/5 border-white/10 text-white h-11" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
          </div>
        </div>

        <div className="pt-4">
          <Button type="submit" disabled={isSubmitting} className="btn-primary w-full h-14 text-lg font-bold">
            {isSubmitting ? <>Booking... <Loader2 className="ml-2 w-5 h-5 animate-spin" /></> : <>Book Now <MessageSquare className="ml-2 w-5 h-5" /></>}
          </Button>
        </div>
        
        <p className="text-[10px] text-center text-white/40 mt-6 font-sans leading-relaxed">
          Clicking "Book Now" will open WhatsApp to finalize your reservation with our dispatch team.
        </p>
      </Form>
    </div>;
}